/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  ProductPrice,
  useProductOptions,
  useMoney,
  useUrl,
} from '@shopify/hydrogen';
import {useEffect, useState} from 'react';
import clsx from 'clsx';

import type {
  PreOrderConfig,
  ProductWithNodes,
  SanityProductIcons,
} from '../../types';

import {hasMultipleProductOptions} from '../../utils/productOptions';
import SelectedVariantAddToCartButton from './buttons/SelectedVariantAddToCart.client';
import ProductOptions from './options/ProductOptions.client';
import InfoAccordion from './InfoAccordion.client';
import Upsell from './Upsell.client';

import {useSiteContext} from '../../hooks/useSiteContext';
import ShippingLineItem from './ShippingLineItem';
import {useAtom} from 'jotai';
import {getShippingUpdatesFormDialogAtom} from '../../jotai';
import useGeo from '../../hooks/useGeo';
import {StarRating} from '../elements/StarRating.client';
import ProductBadge from '../elements/ProductBadge';
import {useInventory} from '../../hooks/useInventory';
import StockistsModal from '../stockists/StockistsModal.client';
import ProductIcons from './ProductIcons';

function ProductPrices({
  storefrontProduct,
}: {
  storefrontProduct: ProductWithNodes;
}) {
  const {selectedVariant} = useProductOptions();
  const {currencyCode} = useMoney(selectedVariant?.priceV2);

  if (!storefrontProduct || !selectedVariant) {
    return null;
  }

  return (
    <>
      <div className="richH5 mt-2 flex items-center gap-x-2">
        <ProductPrice
          data={storefrontProduct}
          priceType="compareAt"
          className="line-through"
          variantId={selectedVariant.id}
        />
        <div
          className={clsx([
            selectedVariant?.compareAtPriceV2 && 'text-orange',
            'flex gap-x-1',
          ])}
          data-testid="product-price"
        >
          <ProductPrice
            data={storefrontProduct}
            variantId={selectedVariant.id}
          />
          {currencyCode && <span>{currencyCode}</span>}
        </div>
      </div>
    </>
  );
}

type ProductWidgetProps = {
  storefrontProduct: ProductWithNodes;
  gid: string;
  body: any[];
  upsell: any;
  upsellShopify?: any;
  preOrderConfig: PreOrderConfig[];
  isOutOfStock?: boolean;
  countryCode: string;
  productIcons?: SanityProductIcons;
};

export default function ProductWidget({
  storefrontProduct,
  gid,
  body,
  upsell,
  upsellShopify,
  preOrderConfig,
  countryCode,
  productIcons,
}: ProductWidgetProps) {
  const [showPreorder, showOutOfStock] = useInventory({
    countryCode,
    gid,
    preOrderConfig,
  });

  const [, setShowGetShippingUpdatesForm] = useAtom(
    getShippingUpdatesFormDialogAtom,
  );

  const {selectedVariant} = useProductOptions();
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );

  const availableForSale = selectedVariant?.availableForSale && !showOutOfStock;

  // Quantity State
  const [quantity, setQuantity] = useState<number>(1);

  // Shipping?
  const {state} = useSiteContext();
  const {currentCountryPathName, isShippable, isManagedByDistributor} =
    useGeo();
  const isShippingException = !isShippable || isManagedByDistributor;

  const [upsellAdded, setUpsellAdded] = useState<boolean>(false);

  // GTM
  const {href, pathname} = useUrl();

  useEffect(() => {
    //add page to GTM dataLayer
    window.dataLayer.push({
      event: 'pageview',
      page: {
        title: storefrontProduct.title,
        path: pathname,
        page: href,
      },
    });
  }, [state]);

  if (!selectedVariant) {
    return null;
  }

  const preOrderExpectedDeliveryDate =
    showPreorder && preOrderConfig[0]?.expectedDeliveryDate
      ? new Date(preOrderConfig[0].expectedDeliveryDate)
      : null;

  const [showStockistsModal, setShowStockistsModal] = useState<boolean>(false);

  const isNZorAU = state?.geoFenceCode === 'NZ' || state?.geoFenceCode === 'AU';

  return (
    <>
      <div
        className={clsx(
          'desc col-start-2 col-end-14',
          'w-full lg:col-start-9 lg:col-end-14',
          'pointer-events-auto z-10',
        )}
      >
        {/* Pre order */}
        {showPreorder && (
          <div className="pb-4">
            <ProductBadge variant="orange" label="Pre-order" />
          </div>
        )}
        {/* Sold out */}
        {!showPreorder && (!availableForSale || showOutOfStock) && (
          <div className="mb-4 mt-2 font-bold">
            <h5 className="blockH11 block w-max rounded-full bg-orange px-4 py-[5px] font-bold text-white">
              Sold out
            </h5>
          </div>
        )}
        {/* Sale */}
        {availableForSale &&
          !showOutOfStock &&
          !showPreorder &&
          selectedVariant?.compareAtPriceV2 && (
            <div className="mb-4 mt-2 font-bold">
              <h5 className="blockH11 block w-max rounded-full bg-orange px-4 py-[5px] font-bold text-white">
                Sale
              </h5>
            </div>
          )}
        {/* Title */}
        {storefrontProduct?.title && (
          <h1 className="blockH5 w-full font-bold lg:w-10/12">
            {storefrontProduct.title}
          </h1>
        )}
        {/* Prices */}
        <ProductPrices storefrontProduct={storefrontProduct} />
        {/* Icons */}
        {productIcons && <ProductIcons icons={productIcons} />}
        {/* Review star rating */}
        <div className="pt-4">
          <StarRating gid={storefrontProduct?.id} scrollToAnchor align="left" />
        </div>
        {/* Product options */}
        {multipleProductOptions && (
          <>
            <ProductOptions />
          </>
        )}
        {/* Information Accordion */}
        {body && <InfoAccordion body={body} />}
        {/* Upsell */}
        {upsell && upsellShopify && (
          <Upsell
            upsell={upsell}
            upsellShopify={upsellShopify}
            upsellAdded={upsellAdded}
            setUpsellAdded={setUpsellAdded}
          />
        )}
        {/* Product actions */}
        <div className="mt-6 flex flex-col gap-y-7 md:max-w-[450px] xl:max-w-[370px]">
          <div
            className={clsx([
              ' flex flex-col items-center justify-between gap-4 xl:flex-row',
              (!state?.shipping || isShippingException) &&
                'pointer-events-none opacity-50',
            ])}
          >
            <div className="richH6 mr-auto flex w-full items-center justify-between gap-x-4 rounded-full bg-light-grey pl-7 font-bold ">
              <button
                onClick={() => setQuantity((prev) => prev - 1)}
                className={clsx([
                  'p-2',
                  quantity === 1 &&
                    'pointer-events-none cursor-not-allowed opacity-50',
                ])}
                aria-label="Remove one item"
                data-testid="decrease-quantity"
              >
                -
              </button>
              <span data-testid="product-quantity">{quantity}</span>
              <button
                onClick={() => setQuantity((prev) => prev + 1)}
                className="p-2"
                aria-label="Add one item"
                data-testid="increase-quantity"
              >
                +
              </button>
              <SelectedVariantAddToCartButton
                quantity={quantity}
                upsellShopify={upsellShopify}
                upsellAdded={upsellAdded}
                setUpsellAdded={setUpsellAdded}
                title={storefrontProduct?.title}
                label={
                  showPreorder
                    ? 'Pre-order'
                    : !availableForSale || showOutOfStock
                    ? 'Sold out'
                    : 'Add to cart'
                }
                isOutOfStock={
                  (!showPreorder && !availableForSale) ||
                  (!showPreorder && showOutOfStock)
                }
                preOrderExpectedDeliveryDate={preOrderExpectedDeliveryDate}
              />
            </div>
          </div>
          {isNZorAU && (
            <button
              onClick={() => setShowStockistsModal(true)}
              className="btn-standard-hollow !w-full"
            >
              Find a retailer
            </button>
          )}
        </div>
        {/* Shipping */}
        {!state?.shipping || isShippingException ? (
          <ShippingLineItem type="error">
            {/* TODO: Make editable in Sanity */}
            {isManagedByDistributor ? (
              <p className="flex flex-col font-normal">
                <strong>
                  Shop with our{' '}
                  <a
                    className="cursor-pointer font-bold text-orange underline"
                    href={isManagedByDistributor.redirect}
                    data-testid="message-go-to-distributor-website"
                  >
                    {currentCountryPathName} Distributor
                  </a>{' '}
                  for shipping to {currentCountryPathName}.
                </strong>
              </p>
            ) : (
              <p
                className="flex flex-col font-normal"
                data-testid="message-non-shippable-product"
              >
                <strong>
                  This product does not currently ship to{' '}
                  {currentCountryPathName}.
                </strong>
                Shipping to this location will be available soon.
                <a
                  onClick={() => setShowGetShippingUpdatesForm(true)}
                  className="cursor-pointer font-bold text-orange underline"
                >
                  Sign up for updates
                </a>
              </p>
            )}
          </ShippingLineItem>
        ) : (
          (showPreorder || availableForSale) &&
          !showOutOfStock && (
            <div
              className="mt-8 flex flex-col gap-y-4"
              data-testid="message-shippable-product"
            >
              {preOrderExpectedDeliveryDate && (
                <div className="flex items-start gap-x-3">
                  <ShippingLineItem type="success">
                    <p>
                      <span className="font-bold">
                        Available for Pre-order:
                      </span>{' '}
                      Expected to be in-stock{' '}
                      <span className="font-bold">
                        {preOrderExpectedDeliveryDate.toLocaleDateString(
                          'en-US',
                          {month: 'long', year: 'numeric'},
                        )}
                      </span>
                    </p>
                  </ShippingLineItem>
                </div>
              )}
              <div className="flex items-start gap-x-3">
                <ShippingLineItem type="success">
                  <p>
                    <span className="font-bold">Shipping:</span> Ships to{' '}
                    {currentCountryPathName}{' '}
                  </p>
                </ShippingLineItem>
              </div>
              <div className="flex items-start gap-x-3">
                <ShippingLineItem type="success">
                  <p>
                    <span className="font-bold">Delivery:</span>{' '}
                    {state?.shippingEst}{' '}
                    {showPreorder && <>for in-stock items</>}
                  </p>
                </ShippingLineItem>
              </div>
              <div className="flex items-start gap-x-3">
                <ShippingLineItem type="success">
                  <p>
                    <span className="font-bold">Dispatch:</span>{' '}
                    {state?.dispatch}
                  </p>
                </ShippingLineItem>
              </div>
            </div>
          )
        )}
      </div>
      {showStockistsModal && (
        <StockistsModal
          isOpen={showStockistsModal}
          setIsOpen={setShowStockistsModal}
        />
      )}
    </>
  );
}
